import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {EditStateComponent} from 'web-shared';
import {filter, takeUntil} from 'rxjs/operators';
import {instanceToInstance} from 'class-transformer';
import {ResourceEditComponent} from '../resource-edit/resource-edit.component';
import {ApiResponse, LeToastrService} from 'common-shared';
import {debounceTime, Subject} from "rxjs";
import {marker} from '@jsverse/transloco-keys-manager/marker';
import {ResourceModel} from "../models/resource.model";
import {ResourceService} from "../resource.service";

@Component({
  selector: 'mes-web-resource-edit-state',
  template: `
    <app-toolbar>
      <app-breadcrumbs>
      </app-breadcrumbs>
      <app-toolbar-edit-buttons [saveState$]="saveState$" [deleteState$]="deleteState$" [showDelete]="!isNew" (save)="isNew ? add() : save()" (delete)="delete()">
        <mes-resource-edit-menu-buttons *ngIf="!isNew"></mes-resource-edit-menu-buttons>
      </app-toolbar-edit-buttons>
    </app-toolbar>
    <mes-web-resource-edit (changed)="onChanged($event)" [resource]="resource" [response]="response" [shouldResetSaveState$]="shouldResetSaveState$" [wasValidated$]="wasValidated$"></mes-web-resource-edit>
  `
})
export class ResourceEditStateComponent extends EditStateComponent implements OnInit {
  resource: ResourceModel;
  response: ApiResponse<any>;
  @ViewChild(ResourceEditComponent, {static: true}) editComponent: ResourceEditComponent;
  wasValidated$: Subject<boolean> = new Subject<boolean>();
  listenCacheUpdate = false;

  constructor(private route: ActivatedRoute, private resourceService: ResourceService, private toastrService: LeToastrService) {
    super();
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
      this.resource = this.route.snapshot.data.entity as ResourceModel;
      if (this.resource == null) {
        this.resource = ResourceModel.CreateDefault();
        this.isNew = true;
      }
    });
    this.notifyIfEntityIsUpdatedElsewhere();
  }

  add() {
    this.wasValidated$.next(true);
    if (this.resource.isValid()) {
      this.resourceService.addNew(this.resource).pipe(takeUntil(this.componentDestroyed)).subscribe({
        next: value => {
          this.router.navigate(['./manufacturing/resource/edit', value.Id]).catch(reason => console.error(reason));
        }, error: value => {
          this.response = value.error;
        }
      });
    }
  }

  save() {
    this.wasValidated$.next(true);
    if (this.resource.isValid()) {
      this.listenCacheUpdate = false;
      this.resourceService.save(this.resource).pipe(takeUntil(this.componentDestroyed)).subscribe({
        next: value => {
          this.resource = instanceToInstance(value);
          this.editComponent.initialize(this.resource);
          this.response = null;
          this.shouldResetSaveState$.next(true);
        }, error: value1 => {
          this.response = value1.error;
        }
      });
    }
  }

  delete() {
    this.resourceService.remove(this.resource).pipe(takeUntil(this.componentDestroyed)).subscribe({
      next: () => {
        this.router.navigate(['./manufacturing/resource']).catch(reason => console.error(reason));
      }, error: value => {
        this.response = value.error;
      }
    });
  }

  onChanged(isPristine: boolean) {
    this.triggerChanges(!isPristine);
  }

  private notifyIfEntityIsUpdatedElsewhere() {
    this.resourceService.query.selectEntity(this.resource.Id).pipe(takeUntil(this.componentDestroyed), debounceTime(1000), filter(resource => {
      if (!resource) return false;
      const listenCacheUpdate = this.listenCacheUpdate;
      if (!listenCacheUpdate) {
        this.listenCacheUpdate = true;
      }
      return listenCacheUpdate;
    })).subscribe(item => {
      this.toastrService.translatedWarning(marker('ime.manufacturing.resources.Resource has been updated elsewhere. Reload page'), marker('ime.manufacturing.resources.Reload'));
    });
  }
}
